import React, { FC } from 'react';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import { TransparentButton } from '~Styles';
import { NavLink, useLocation } from 'react-router-dom';
import { useButton } from '~Hooks';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

const Wrapper = styled(TransparentButton)`
  text-transform: none;
  padding: 0;
  margin: 4px;
  &:active {
    background-color: var(--colorBlueGray1100);
  }
`;

const StyledNavLink = styled(NavLink)<{ isOnThisRoute: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--colorBlueGray1100);
    border-radius: 8px;
    transform: scaleX(${(props) => (props.isOnThisRoute ? 1 : 0)});
    transform-origin: center;
    transition: transform 0.2s ease-out;
    z-index: -1;
  }
`;

const StyledIcon = styled(Icon)`
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }
`;

export interface MenuItemType {
  id: string;
  label: string;
  iconOutlined: string;
  iconFilled: string;
  allowedToRender: boolean;

  /** Active for the links that opens a modal instead of re-routing */
  active?: boolean;

  /** One of the following will always be present */
  link?: string;
  onClick?: () => void;
}

interface Props {
  item: MenuItemType;
  closeMainMenu: () => void;
}

/** One button in the sidebar menu */
const MenuItem: FC<Props> = ({ item, closeMainMenu }) => {
  const location = useLocation();
  const isOnThisRoute = item.active || (item.link ? location.pathname === item.link : false);

  const itemButton = useButton({
    onClick: () => {
      if (item.onClick) {
        item.onClick();
        return;
      }

      closeMainMenu();
    },
  });

  if (!item.allowedToRender) {
    return null;
  }

  return (
    <Wrapper display="flex" alignItems="center" justifyContent="flex-start" {...itemButton}>
      <StyledNavLink to={item.link ?? '#'} isOnThisRoute={isOnThisRoute}>
        <StyledIcon
          src={isOnThisRoute ? item.iconFilled : item.iconOutlined}
          width="24px"
          height="24px"
          color="var(--colorBlueGray200)"
        />

        <Paragraph
          fontWeight={600}
          level={5}
          color={isOnThisRoute ? 'var(--colorWhite)' : 'var(--colorBlueGray200)'}
          bottom={0}>
          {item.label}
        </Paragraph>
      </StyledNavLink>
    </Wrapper>
  );
};

export default MenuItem;
